import React, { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2, ShieldCheck } from 'lucide-react';
import Button from '../components/ui/Button';
import { getSubscriptionErrorMessage, verifySubscription } from '../services/subscription';

type CallbackState = 'processing' | 'success' | 'failed';

const BillingCallbackPage: React.FC = () => {
  const [state, setState] = useState<CallbackState>('processing');
  const [message, setMessage] = useState('GabMic is verifying this payment with Paystack.');

  useEffect(() => {
    let isMounted = true;
    const params = new URLSearchParams(window.location.search);
    const reference = params.get('reference') || params.get('trxref') || '';

    const verify = async () => {
      if (!reference) {
        setState('failed');
        setMessage('Paystack did not return a payment reference.');
        return;
      }

      try {
        await verifySubscription(reference);
        if (!isMounted) return;
        setState('success');
        setMessage('Payment verified. Your GabMic plan is now updated.');
      } catch (error) {
        if (!isMounted) return;
        setState('failed');
        setMessage(getSubscriptionErrorMessage(error));
      }
    };

    verify();

    return () => {
      isMounted = false;
    };
  }, []);

  const Icon = state === 'processing' ? Loader2 : state === 'success' ? CheckCircle2 : AlertCircle;

  return (
    <div className="page-stack" style={{ maxWidth: '720px', margin: '0 auto' }}>
      <section className="premium-card" style={{ padding: '2rem', textAlign: 'center' }}>
        <span className="icon-tile" style={{ width: '54px', height: '54px', marginBottom: '1rem' }}>
          <Icon
            size={24}
            style={state === 'processing' ? { animation: 'gabmic-spin 1s linear infinite' } : undefined}
          />
        </span>
        <h1 style={{ fontSize: '2rem', marginBottom: '0.65rem' }}>
          {state === 'processing' ? 'Verifying payment' : state === 'success' ? 'Payment verified' : 'Payment not verified'}
        </h1>
        <p style={{ margin: '0 auto 1.4rem', fontSize: '1rem' }}>{message}</p>

        <div style={{ display: 'flex', justifyContent: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          {state === 'success' ? (
            <>
              <a href="/settings"><Button variant="primary">View billing</Button></a>
              <a href="/upload"><Button variant="outline">Upload data</Button></a>
            </>
          ) : (
            <>
              <a href="/pricing"><Button variant="primary">Back to pricing</Button></a>
              <a href="/settings"><Button variant="outline">Billing settings</Button></a>
            </>
          )}
        </div>
      </section>

      <div className="premium-card" style={{ padding: '1rem', display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
        <ShieldCheck size={18} color="var(--gabmic-orange)" style={{ marginTop: '0.2rem' }} />
        <p style={{ margin: 0, fontSize: '0.9rem' }}>
          This page does not trust URL status values. GabMic confirms the payment directly with Paystack before changing your plan.
        </p>
      </div>
    </div>
  );
};

export default BillingCallbackPage;
